import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  signal,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowDown,
  lucideArrowUp,
  lucideChevronsUpDown,
  lucideReceipt,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmBadgeImports, type BadgeVariants } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTableImports } from '@spartan-ng/helm/table';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { formatCurrency } from '../../utils/currency.utils';
import { VoucherDetailsComponent } from '../voucher-details/voucher-details.component';
import { TablePaginationComponent } from '../table-pagination/table-pagination.component';

export interface RedemptionRow {
  redemption_id: number;
  redeemed_at: string;
  voucher_code: string;
  amount: number;
  balance_before?: number | null;
  balance_after?: number | null;
  /** What the network owes the member for it, under the network's settlement rule. */
  payout_cents?: number | null;
  /** Monthly settlement with the network ("YYYY-MM"); null while still open. */
  settlement_period?: string | null;
  settlement_status?: 'draft' | 'exported' | 'paid' | 'cancelled' | null;
  member_company_name?: string | null;
  // Voucher type, mirroring the badges in the Gutscheine list
  voucher_product_id?: string | null;
  voucher_product_name?: string | null;
  is_internal?: boolean;
}

export type RedemptionSortField =
  'redeemed_at' | 'voucher_code' | 'member_company_name' | 'amount' | 'payout_cents' | 'settlement';

/**
 * One rendered row, with every string the template needs already built.
 *
 * Formatting used to happen in template expressions, which meant a
 * `toLocaleString` and several currency conversions per row on every change
 * detection pass. Now it runs once per row, only when the page actually changes.
 */
interface RedemptionView {
  row: RedemptionRow;
  date: string;
  amount: string;
  balanceBefore: string;
  balanceAfter: string;
  payout: string | null;
  deductionTooltip: string;
  statusLabel: string;
  statusVariant: BadgeVariants['variant'];
}

const DATE_FORMAT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Locale-aware collator, built once — constructing one per comparison is slow. */
const COLLATOR = new Intl.Collator('de', { sensitivity: 'base', numeric: true });

@Component({
  selector: 'app-redemption-table',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertImports,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmDialogImports,
    HlmEmptyImports,
    HlmSkeletonImports,
    HlmTableImports,
    HlmTooltipImports,
    TablePaginationComponent,
    VoucherDetailsComponent,
  ],
  providers: [
    provideIcons({
      lucideArrowDown,
      lucideArrowUp,
      lucideChevronsUpDown,
      lucideReceipt,
    }),
  ],
  // All state below is signals, so the component only re-renders when one of
  // them actually changes. Under the previous eager strategy this table was
  // re-checked on every event anywhere in the app — and since the dashboard
  // keeps all three of its tables mounted (tabs hide with [hidden], they are
  // not destroyed), that work happened three times over.
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './redemption-table.component.html',
})
export class RedemptionTableComponent {
  readonly records = input<RedemptionRow[]>([]);
  readonly showMemberColumn = input(false);
  readonly showPayout = input(false);
  readonly showStatusColumn = input(true);
  readonly searchQuery = input('');
  readonly emptyMessage = input('Keine Einlösungen vorhanden.');
  readonly pageSize = input(20);
  /** Renders skeleton rows instead of the empty state while the data loads. */
  readonly loading = input(false);

  /** Row placeholders for the loading state. */
  readonly skeletonRows = [1, 2, 3, 4, 5];

  readonly selectedVoucherCode = signal<string | null>(null);
  readonly showVoucherModal = signal(false);
  readonly sortField = signal<RedemptionSortField>('redeemed_at');
  readonly sortOrder = signal<'asc' | 'desc'>('desc');

  /** Back to page 1 whenever the underlying data or the search term changes. */
  readonly currentPage = linkedSignal({
    source: () => ({ records: this.records(), query: this.searchQuery() }),
    computation: () => 1,
  });

  // ── Search filter ────────────────────────────────────────

  readonly filteredRecords = computed(() => {
    const q = this.searchQuery().trim().toLowerCase();
    const records = this.records();
    if (!q) return records;
    return records.filter((r) => r.voucher_code.toLowerCase().includes(q));
  });

  // ── Sorting ──────────────────────────────────────────────

  readonly sortedRecords = computed(() => {
    const field = this.sortField();
    const direction = this.sortOrder() === 'asc' ? 1 : -1;

    // The sort key is derived once per row rather than inside the comparator —
    // otherwise every one of the O(n log n) comparisons re-parsed a date string.
    const keyed = this.filteredRecords().map((row) => ({ row, key: sortKey(row, field) }));

    keyed.sort((a, b) => {
      // Text columns need locale rules; "Ärzte" belongs next to "Arzt", not after "Z".
      const comparison =
        typeof a.key === 'string' && typeof b.key === 'string'
          ? COLLATOR.compare(a.key, b.key)
          : Number(a.key) - Number(b.key);

      // Ties keep a stable, predictable order instead of the browser's arbitrary one.
      return comparison !== 0 ? comparison * direction : a.row.redemption_id - b.row.redemption_id;
    });

    return keyed.map((entry) => entry.row);
  });

  handleSort(field: RedemptionSortField) {
    if (this.sortField() === field) {
      this.sortOrder.update((order) => (order === 'asc' ? 'desc' : 'asc'));
    } else {
      this.sortField.set(field);
      this.sortOrder.set(field === 'redeemed_at' ? 'desc' : 'asc');
    }
    this.currentPage.set(1);
  }

  // ── Pagination ───────────────────────────────────────────

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredRecords().length / this.pageSize())),
  );

  readonly pagedRecords = computed<RedemptionView[]>(() => {
    // Clamped: a shrinking result set must not leave the view on an empty page.
    const page = Math.min(this.currentPage(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.sortedRecords()
      .slice(start, start + this.pageSize())
      .map((row) => toView(row));
  });

  handlePageChange(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  // ── Header state ─────────────────────────────────────────

  /** Header icon: neutral for inactive columns, direction arrow for the sorted one. */
  sortIcon(field: RedemptionSortField): string {
    if (this.sortField() !== field) return 'lucideChevronsUpDown';
    return this.sortOrder() === 'asc' ? 'lucideArrowUp' : 'lucideArrowDown';
  }

  readonly columnCount = computed(
    () =>
      5 + // date, code, balance_before, balance_after, amount
      (this.showMemberColumn() ? 1 : 0) +
      (this.showPayout() ? 1 : 0) +
      (this.showStatusColumn() ? 1 : 0),
  );

  // ── Interaction ──────────────────────────────────────────

  onDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.closeVoucherModal();
    }
  }

  openVoucherModal(code: string) {
    this.selectedVoucherCode.set(code);
    this.showVoucherModal.set(true);
  }

  closeVoucherModal() {
    this.showVoucherModal.set(false);
    this.selectedVoucherCode.set(null);
  }
}

function sortKey(row: RedemptionRow, field: RedemptionSortField): string | number {
  switch (field) {
    case 'redeemed_at':
      // Parsed rather than compared as a string: the API is not guaranteed to
      // return a uniform ISO format, and "2026-1-5" would sort after "2026-10-…".
      return new Date(row.redeemed_at).getTime();
    case 'voucher_code':
      return row.voucher_code;
    case 'member_company_name':
      return row.member_company_name ?? '';
    case 'amount':
      return row.amount;
    case 'payout_cents':
      return row.payout_cents ?? -1;
    case 'settlement':
      return statusOf(row).label;
  }
}

/** "2026-09" -> "09/2026" */
function monthLabel(period: string): string {
  const [year, month] = period.split('-');
  return `${month}/${year}`;
}

/** Member redemptions are settled by the network once a month. */
function statusOf(row: RedemptionRow): { label: string; variant: BadgeVariants['variant'] } {
  if (row.settlement_period && row.settlement_status === 'paid') {
    return { label: `Bezahlt ${monthLabel(row.settlement_period)}`, variant: 'solid' };
  }
  if (row.settlement_period && row.settlement_status !== 'cancelled') {
    return { label: `In Abrechnung ${monthLabel(row.settlement_period)}`, variant: 'secondary' };
  }
  return { label: 'Offen', variant: 'outline' };
}

function toView(row: RedemptionRow): RedemptionView {
  const deduction = row.payout_cents != null ? row.amount - row.payout_cents : 0;

  return {
    row,
    date: DATE_FORMAT.format(new Date(row.redeemed_at)),
    amount: formatCurrency(row.amount),
    balanceBefore: row.balance_before != null ? `${formatCurrency(row.balance_before)} €` : '–',
    balanceAfter: row.balance_after != null ? `${formatCurrency(row.balance_after)} €` : '–',
    payout: row.payout_cents != null ? `${formatCurrency(row.payout_cents)} €` : null,
    deductionTooltip:
      deduction > 0
        ? `Abzug laut Abrechnungsregel des Netzwerks: ${formatCurrency(deduction)} €`
        : '',
    statusLabel: statusOf(row).label,
    statusVariant: statusOf(row).variant,
  };
}
